import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ActionSidePanel from 'src/components/design-system/ActionSidePanel';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useConfirm from 'src/hooks/useConfirm';
import useWindowSize from 'src/hooks/useWindowSize';
import { ShareCopySelectionState } from 'src/types/view_shopping_lists/ShareCopyList';
import { ShoppingList } from 'src/types/view_shopping_lists/ShoppingList';
import ShareCopyListRecipientsContent from './ShareCopyListRecipientsContent';

const EMPTY_SELECTION: ShareCopySelectionState = {
  selectedUserIds: [],
  folderAddUsers: [],
  folderRemoveUsers: [],
};

export interface ShareShoppingListSidePanelProps {
  isOpen: boolean;
  shoppingList?: ShoppingList | null;
  folderIds: number[];
  isSharing?: boolean;
  onClose: () => void;
  onShare: (payload: {
    folderIds: number[];
    folderAddUsers: number[];
    folderRemoveUsers: number[];
  }) => Promise<void>;
}

const ShareShoppingListSidePanel: FC<ShareShoppingListSidePanelProps> = ({
  isOpen,
  shoppingList = null,
  folderIds,
  isSharing = false,
  onClose,
  onShare,
}) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const { width: viewportWidth } = useWindowSize();
  const [selection, setSelection] = useState<ShareCopySelectionState>(EMPTY_SELECTION);

  useEffect(() => {
    if (!isOpen) {
      setSelection(EMPTY_SELECTION);
    }
  }, [isOpen]);

  const hasSelectionChanges =
    selection.folderAddUsers.length > 0 || selection.folderRemoveUsers.length > 0;

  const confirmDiscardChanges = useCallback(async (): Promise<boolean> => {
    if (!hasSelectionChanges) {
      return true;
    }

    return confirm({
      title: t('Unsaved Changes'),
      content: t('You have unsaved changes. Do you want to discard them?'),
      cancelButtonText: t('Cancel'),
      confirmButtonText: t('Yes, Discard'),
      width: '500px',
    });
  }, [confirm, hasSelectionChanges, t]);

  const handleClose = useCallback(async () => {
    if (isSharing) {
      return;
    }

    const shouldClose = await confirmDiscardChanges();
    if (shouldClose) {
      onClose();
    }
  }, [confirmDiscardChanges, isSharing, onClose]);

  const handleShare = useCallback(async () => {
    if (folderIds.length === 0) {
      toast.error(t('Please select list(s) to Share.'));
      return;
    }

    if (!hasSelectionChanges) {
      toast.error(t('Please select at least one recipient to add or remove.'));
      return;
    }

    try {
      await onShare({
        folderIds,
        folderAddUsers: selection.folderAddUsers,
        folderRemoveUsers: selection.folderRemoveUsers,
      });
    } catch {
      toast.error(t('Failed to share shopping list'));
    }
  }, [
    folderIds,
    hasSelectionChanges,
    onShare,
    selection.folderAddUsers,
    selection.folderRemoveUsers,
    t,
  ]);

  const listTitle = shoppingList?.title?.trim();
  const showListNameInTitle = folderIds.length <= 1 && Boolean(listTitle);
  const panelTitle = showListNameInTitle
    ? `${t('Share List')} - ${listTitle}`
    : t('Share List');

  const saveDisabled = folderIds.length === 0 || !hasSelectionChanges;

  const panelWidth = useMemo(
    () => (viewportWidth >= 768 ? 'min(92vw, 1200px)' : '100%'),
    [viewportWidth]
  );

  const saveDisabledTooltip = (
    <PopUpContentWrapper type="Information" contents={[t('No changes made')]} />
  );

  return (
    <ActionSidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title={panelTitle}
      titleTooltip={listTitle}
      showTitleTooltip={showListNameInTitle}
      onSave={handleShare}
      onCancel={handleClose}
      saveLabel={t('Share')}
      saveDisabled={saveDisabled}
      saveLoading={isSharing}
      cancelDisabled={isSharing}
      saveDisabledTooltip={saveDisabledTooltip}
      width={panelWidth}
      className="bg-[#F1F1F1] share-copy-shopping-list-panel"
      contentClassName="min-h-0 overflow-hidden flex flex-col"
    >
      {isOpen ? (
        <ShareCopyListRecipientsContent
          mode="share"
          folderIds={folderIds}
          onSelectionChange={setSelection}
        />
      ) : null}
    </ActionSidePanel>
  );
};

export default ShareShoppingListSidePanel;
